import React, { useEffect, useState } from 'react';
import { api } from '../api/client';

interface Transaction {
  id: string;
  type: string;
  amount: number;
  currency: string;
  description: string;
  status: string;
  created_at: string;
}

interface OverviewData {
  total_income: number;
  total_expenses: number;
  balance: number;
  recent: Transaction[];
}

export default function FinanceOverview() {
  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      setError('');
      try {
        const res = await api.get('/api/transactions/overview');
        setOverview(res.data || res);
      } catch {
        const res = await api.get('/api/transactions');
        const list: Transaction[] = res.data || [];
        setTransactions(list);
        const totalIncome = list
          .filter((t) => t.type === 'income')
          .reduce((sum, t) => sum + t.amount, 0);
        const totalExpenses = list
          .filter((t) => t.type === 'expense')
          .reduce((sum, t) => sum + t.amount, 0);
        setOverview({
          total_income: totalIncome,
          total_expenses: totalExpenses,
          balance: totalIncome - totalExpenses,
          recent: list.slice(0, 5),
        });
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const recent = overview?.recent ?? [];

  return (
    <div>
      <h1 style={styles.heading}>Finance Overview</h1>
      {error && <div style={styles.error}>{error}</div>}

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <div style={styles.statsGrid}>
            <StatCard title="Total Income" value={overview?.total_income ?? 0} prefix="$" />
            <StatCard title="Total Expenses" value={overview?.total_expenses ?? 0} prefix="$" />
            <StatCard title="Balance" value={overview?.balance ?? 0} prefix="$" />
          </div>

          <h2 style={styles.subheading}>Recent Transactions</h2>
          <div style={styles.card}>
            {recent.length === 0 && <div style={styles.empty}>No transactions yet</div>}
            {recent.map((t) => (
              <div key={t.id} style={styles.row}>
                <div>
                  <div style={styles.rowTitle}>{t.description}</div>
                  <div style={styles.rowMeta}>
                    {t.type} • {t.status}
                  </div>
                </div>
                <div style={{ fontWeight: 600 }}>
                  {t.currency} {t.amount.toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ title, value, prefix }: { title: string; value: number; prefix?: string }) {
  return (
    <div style={styles.statCard}>
      <div style={styles.statLabel}>{title}</div>
      <div style={styles.statValue}>
        {prefix}
        {value.toFixed(2)}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  heading: { fontSize: 20, fontWeight: 600, marginBottom: 16 },
  error: { background: '#fef2f2', color: '#991b1b', padding: 10, borderRadius: 8, marginBottom: 12 },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 32 },
  statCard: { background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #e5e7eb' },
  statLabel: { fontSize: 12, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 },
  statValue: { fontSize: 28, fontWeight: 600, marginTop: 8 },
  subheading: { fontSize: 16, fontWeight: 600, marginBottom: 12 },
  card: { background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb' },
  empty: { padding: 20, color: '#9ca3af' },
  row: { padding: '12px 16px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  rowTitle: { fontWeight: 500, fontSize: 14 },
  rowMeta: { fontSize: 12, color: '#6b7280' },
};
